
// utils/getSnappedPoint.js
import * as THREE from 'three';
import { getActiveFloorPlanId, filterSnapPointsByFloorPlan, getGlobalDocumentBounds, getGlobalSnapTargets, getGlobalSnapMode, getGlobalEnabledSnapTypes, getGlobalLineSegments } from '../../utils/getActiveFloorPlanId';

// ✅ snapTargets'a göre snap noktalarını filtrele (parametre yoksa global'den oku)
// + enabledSnapTypes'a göre snap tip filtreleme
function filterBySnapTargets(snapPoints, snapTargets) {
  const targets = snapTargets || getGlobalSnapTargets();
  const enabledTypes = getGlobalEnabledSnapTypes();

  return snapPoints.filter(sp => {
    // 1) Kaynak filtresi (mevcut)
    if (targets && sp.source) {
      if (sp.source === 'mimariCizim' && !targets.mimariCizim) return false;
      if (sp.source === 'kullaniciCizim' && !targets.kullaniciCizim) return false;
      if (sp.source === 'tesisatCizim' && !targets.tesisatCizim) return false;
      if (sp.source === 'dokumanlar' && !targets.dokumanlar) return false;
    }

    // 2) Snap tip filtresi
    if (enabledTypes && sp.type) {
      if (['start', 'end', 'startpoint', 'endpoint'].includes(sp.type)) {
        return enabledTypes.endpoint !== false;
      }
      if (sp.type === 'diagonal') return enabledTypes.quadrant !== false;
      if (sp.type === 'insertion') return enabledTypes.node !== false;
      if (enabledTypes[sp.type] !== undefined) {
        return enabledTypes[sp.type] !== false;
      }
    }
    return true;
  });
}

// ⚡ PERF: snap sorgusu HER mouse hareketinde çalışır. İki israf giderilir:
//  1) Projeksiyon için tek yeniden-kullanılan Vector3 (döngüde clone YOK) → binlerce
//     Vector3 allocation/frame ortadan kalkar (heap sawtooth = GC baskısı = kasma).
//  2) Filtre sonucu memoize: (snapPoints, floorPlanId, targets, enabledTypes) sabitken
//     yeniden filtreleme yapma — sürükleme boyunca bunlar değişmez.
const _projVec = new THREE.Vector3();
// ⚡ Viewport culling: görüş alanına girmeyen snap noktalarını projelendirmeden atla.
const _snapFrustum = new THREE.Frustum();
const _snapViewProj = new THREE.Matrix4();
let _filterMemo = { snapPoints: null, floorPlanId: undefined, targets: undefined, enabledTypes: undefined, result: null };

function getFilteredSnapPoints(snapPoints, activeFloorPlanId, snapTargets, hasBounds) {
  const targets = snapTargets || getGlobalSnapTargets();
  const enabledTypes = getGlobalEnabledSnapTypes();
  if (
    _filterMemo.result &&
    _filterMemo.snapPoints === snapPoints &&
    _filterMemo.floorPlanId === activeFloorPlanId &&
    _filterMemo.targets === targets &&
    _filterMemo.enabledTypes === enabledTypes
  ) {
    return _filterMemo.result;
  }
  let fp = hasBounds ? filterSnapPointsByFloorPlan(snapPoints, activeFloorPlanId) : snapPoints;
  fp = filterBySnapTargets(fp, targets);
  _filterMemo = { snapPoints, floorPlanId: activeFloorPlanId, targets, enabledTypes, result: fp };
  return fp;
}

// ✅ Snap type priority: lower number = higher priority (AutoCAD OSNAP sıralaması)
const SNAP_PRIORITY = {
  start: 1, end: 1, startpoint: 1, endpoint: 1,
  vertex: 2,
  midpoint: 3,
  center: 4,
  node: 5, insertion: 5,
  quadrant: 6, diagonal: 6,
  intersection: 7,
  perpendicular: 8,
  tangent: 9,
  extension: 10,
  nearest: 11,
};

function getSnapPriority(type) {
  return SNAP_PRIORITY[type] ?? 50; // Unknown types get lowest priority
}

// ✅ Nearest point on a line segment (clamped to segment bounds)
function nearestPointOnSegment(p, a, b) {
  const ab = new THREE.Vector3().subVectors(b, a);
  const ap = new THREE.Vector3().subVectors(p, a);
  const lenSq = ab.dot(ab);
  if (lenSq < 1e-10) return { point: a.clone(), t: 0 };
  const t = Math.max(0, Math.min(1, ap.dot(ab) / lenSq));
  return { point: new THREE.Vector3().addVectors(a, ab.multiplyScalar(t)), t };
}

// ✅ Dynamic snap points: nearest/perpendicular from cursor to stored line segments
function getDynamicSnapPoints(worldPoint, snapTargets) {
  const enabledTypes = getGlobalEnabledSnapTypes();
  const nearestEnabled = !enabledTypes || enabledTypes.nearest !== false;
  const perpEnabled = !enabledTypes || enabledTypes.perpendicular !== false;
  if (!nearestEnabled && !perpEnabled) return [];

  const segments = getGlobalLineSegments();
  if (!segments || segments.length === 0) return [];

  const targets = snapTargets || getGlobalSnapTargets();
  const dynamicPoints = [];

  for (const seg of segments) {
    // Source filter
    if (targets && seg.source) {
      if (seg.source === 'mimariCizim' && !targets.mimariCizim) continue;
      if (seg.source === 'kullaniciCizim' && !targets.kullaniciCizim) continue;
      if (seg.source === 'tesisatCizim' && !targets.tesisatCizim) continue;
      if (seg.source === 'dokumanlar' && !targets.dokumanlar) continue;
    }

    const { point, t } = nearestPointOnSegment(worldPoint, seg.start, seg.end);

    // Skip if projection is at segment endpoints (already covered by endpoint snap)
    if (t < 0.01 || t > 0.99) continue;

    if (nearestEnabled) {
      dynamicPoints.push({
        position: point,
        type: 'nearest',
        floorPlanId: seg.floorPlanId,
        source: seg.source,
      });
    } else if (perpEnabled) {
      dynamicPoints.push({
        position: point,
        type: 'perpendicular',
        floorPlanId: seg.floorPlanId,
        source: seg.source,
      });
    }
  }

  return dynamicPoints;
}

export function getSnappedPoint(
  mouseNDC,
  worldPoint,
  snapPoints,
  camera,
  renderer,
  options = {}
) {
  const { objectSnap = true, documentBounds = null, snapTargets = null } = options;
  // ✅ snapMode her zaman global'den okunur (stale closure sorununu önler)
  const snapMode = getGlobalSnapMode();

  if (!snapMode || !objectSnap) {
    return { finalPoint: worldPoint.clone(), snapped: false, snapSource: null };
  }

  // ✅ Doküman sınırlarına göre snap noktalarını filtrele (memoized — bkz. getFilteredSnapPoints)
  const bounds = documentBounds || getGlobalDocumentBounds();
  const hasBounds = !!(bounds && bounds.length > 0);
  const activeFloorPlanId = hasBounds ? getActiveFloorPlanId(worldPoint, bounds) : undefined;
  const filteredSnapPoints = getFilteredSnapPoints(snapPoints, activeFloorPlanId, snapTargets, hasBounds);

  const canvasW = renderer.domElement.clientWidth;
  const canvasH = renderer.domElement.clientHeight;
  const mouseX = (mouseNDC.x * 0.5 + 0.5) * canvasW;
  const mouseY = (1 - (mouseNDC.y * 0.5 + 0.5)) * canvasH;

  // ⚡ Viewport frustum: sadece görünen snap noktalarını işle (off-screen atlanır)
  camera.updateMatrixWorld();
  camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
  _snapFrustum.setFromProjectionMatrix(
    _snapViewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
  );

  // ✅ Collect ALL static snap candidates within threshold (clone yok → _projVec yeniden kullanılır)
  const candidates = [];
  for (let sp of filteredSnapPoints) {
    if (!_snapFrustum.containsPoint(sp.position)) continue; // ⚡ görünmeyen noktayı atla
    _projVec.copy(sp.position).project(camera);
    const screenX = (_projVec.x * 0.5 + 0.5) * canvasW;
    const screenY = (1 - (_projVec.y * 0.5 + 0.5)) * canvasH;
    const dx = screenX - mouseX;
    const dy = screenY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 10) {
      candidates.push({ sp, distance, priority: getSnapPriority(sp.type) });
    }
  }

  // ✅ Also collect dynamic snap candidates (nearest/perpendicular)
  const dynamicPoints = getDynamicSnapPoints(worldPoint, snapTargets);
  for (const dp of dynamicPoints) {
    _projVec.copy(dp.position).project(camera);
    const screenX = (_projVec.x * 0.5 + 0.5) * canvasW;
    const screenY = (1 - (_projVec.y * 0.5 + 0.5)) * canvasH;
    const dx = screenX - mouseX;
    const dy = screenY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 10) {
      candidates.push({ sp: dp, distance, priority: getSnapPriority(dp.type) });
    }
  }

  // ✅ Sort: priority first (lower = better), then distance (closer = better)
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.priority - b.priority || a.distance - b.distance);
    const best = candidates[0];
    return {
      finalPoint: best.sp.position.clone(),
      snapped: true,
      snapSource: best.sp,
    };
  }

  return { finalPoint: worldPoint.clone(), snapped: false, snapSource: null };
}
export function getSnappedPointForLightBuat(
  mouseNDC,
  worldPoint,
  snapPoints,
  camera,
  renderer,
  options = {}
) {
  const { objectSnap = true, documentBounds = null, snapTargets = null } = options;
  const snapMode = getGlobalSnapMode();

  if (!snapMode || !objectSnap) {
    return { finalPoint: worldPoint.clone(), snapped: false, snapSource: null };
  }

  // ✅ Doküman sınırlarına göre snap noktalarını filtrele (global bounds kullanılır)
  let filteredSnapPoints = snapPoints;
  const bounds = documentBounds || getGlobalDocumentBounds();
  if (bounds && bounds.length > 0) {
    const activeFloorPlanId = getActiveFloorPlanId(worldPoint, bounds);
    filteredSnapPoints = filterSnapPointsByFloorPlan(snapPoints, activeFloorPlanId);
  }

  // ✅ snapTargets'a göre filtrele
  filteredSnapPoints = filterBySnapTargets(filteredSnapPoints, snapTargets);

  let snapped = false;
  let finalPoint = worldPoint.clone();
  let snapSource = null;
  for (let sp of filteredSnapPoints) {
    if (sp.type !== "buatLightingSquare") continue
    const rect = renderer.domElement.getBoundingClientRect();

    const projected = sp.position.clone();
    projected.project(camera);

    const screenX = (projected.x * 0.5 + 0.5) * rect.width + rect.left;
    const screenY = (1 - (projected.y * 0.5 + 0.5)) * rect.height + rect.top;

    const mouseX = (mouseNDC.x * 0.5 + 0.5) * rect.width + rect.left;
    const mouseY = (1 - (mouseNDC.y * 0.5 + 0.5)) * rect.height + rect.top;


    const dx = screenX - mouseX;
    const dy = screenY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    

    if (distance < 10) {
      finalPoint = sp.position.clone();
      snapSource = sp;
      snapped = true;
      
      break;
    }
  }

  return { finalPoint, snapped, snapSource };
}
export function getSnappedPointForPanel(
  mouseNDC,
  worldPoint,
  snapPoints,
  camera,
  renderer,
  options = {}
) {
  const { forWhere = "draw", objectSnap = true, documentBounds = null, snapTargets = null } = options;
  const snapMode = getGlobalSnapMode();
  if (!snapMode || !objectSnap) {
    return { finalPoint: worldPoint.clone(), snapped: false, snapSource: null };
  }

  // ✅ Doküman sınırlarına göre snap noktalarını filtrele (global bounds kullanılır)
  let filteredSnapPoints = snapPoints;
  const bounds = documentBounds || getGlobalDocumentBounds();
  if (bounds && bounds.length > 0) {
    const activeFloorPlanId = getActiveFloorPlanId(worldPoint, bounds);
    filteredSnapPoints = filterSnapPointsByFloorPlan(snapPoints, activeFloorPlanId);
  }

  // ✅ snapTargets'a göre filtrele
  filteredSnapPoints = filterBySnapTargets(filteredSnapPoints, snapTargets);

  let snapped = false;
  let finalPoint = worldPoint.clone();
  let snapSource = null;
  let minDistance = Infinity;
  let bestSnap = null;

  for (let sp of filteredSnapPoints) {
    dongu(sp)

    /*     if (forWhere === "panel" )  {
          if(sp.connection)dongu(sp)
        } else if(forWhere === "draw" ){
          dongu(sp)
        } */
  }

  function dongu(sp) {
    const projected = sp.position.clone().project(camera);
    const screenX = (projected.x * 0.5 + 0.5) * renderer.domElement.clientWidth;
    const screenY = (1 - (projected.y * 0.5 + 0.5)) * renderer.domElement.clientHeight;

    const mouseX = (mouseNDC.x * 0.5 + 0.5) * renderer.domElement.clientWidth;
    const mouseY = (1 - (mouseNDC.y * 0.5 + 0.5)) * renderer.domElement.clientHeight;

    const dx = screenX - mouseX;
    const dy = screenY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < minDistance && distance < 10) {
      minDistance = distance;
      bestSnap = sp;
    }
    if (bestSnap) {
      finalPoint = bestSnap.position.clone();
      snapSource = bestSnap;
      snapped = true;
    }
  }
  return { finalPoint, snapped, snapSource };
}
// utils/getSnappedPointLightFixture.js
export function getSnappedPointLightFixture(
  mouseNDC,
  worldPoint,
  snapPoints,
  camera,
  renderer,
  options = {}
) {
  const { documentBounds = null, snapTargets = null } = options;
  const snapMode = getGlobalSnapMode();

  if (!snapMode) {
    return { finalPoint: worldPoint.clone(), snapped: false, snapSource: null };
  }

  // ✅ Doküman sınırlarına göre snap noktalarını filtrele (global bounds kullanılır)
  let filteredSnapPoints = snapPoints;
  const bounds = documentBounds || getGlobalDocumentBounds();
  if (bounds && bounds.length > 0) {
    const activeFloorPlanId = getActiveFloorPlanId(worldPoint, bounds);
    filteredSnapPoints = filterSnapPointsByFloorPlan(snapPoints, activeFloorPlanId);
  }

  // ✅ snapTargets'a göre filtrele
  filteredSnapPoints = filterBySnapTargets(filteredSnapPoints, snapTargets);

  let snapped = false;
  let finalPoint = worldPoint.clone();
  let snapSource = null;

  // Snap noktaları üzerinde döngü başlatıyoruz
  for (let sp of filteredSnapPoints) {
    // Sadece lightingBuat tipi için işlem yapıyoruz
    if (sp.type !== 'lightingBuat') continue;

    const rect = renderer.domElement.getBoundingClientRect();
    const projected = sp.position.clone();
    projected.project(camera);

    const screenX = (projected.x * 0.5 + 0.5) * rect.width + rect.left;
    const screenY = (1 - (projected.y * 0.5 + 0.5)) * rect.height + rect.top;

    const mouseX = (mouseNDC.x * 0.5 + 0.5) * rect.width + rect.left;
    const mouseY = (1 - (mouseNDC.y * 0.5 + 0.5)) * rect.height + rect.top;

    const dx = screenX - mouseX;
    const dy = screenY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 10) {
      finalPoint = sp.position.clone();
      snapSource = sp;
      snapped = true;
      break;
    }
  }

  return { finalPoint, snapped, snapSource };
}
